<template>
  <div class="eval-results">
    <div v-if="groupedResults.length" class="eval-results__groups">
      <div v-for="group in groupedResults" :key="group.runId || 'no-run-id'" class="eval-results__group">
        <div class="eval-results__group-header">Run: {{ group.runId || "N/A" }}</div>
        <ul class="eval-results__list">
          <li class="eval-results__item" v-for="result in group.results" :key="result.evalRequestId + '-' + result.judgeType">
            <div class="eval-results__left">
              <div v-if="result.score != null" class="eval-results__score" :class="getScoreClass(result.score)">{{ result.score }}</div>
              <div v-else-if="result.pass != null" class="eval-results__passfail">
                <IconEmbedded v-if="result.pass" name="check_4-5" :size="18" color="rgba(61, 133, 40, 1)" />
                <span v-else class="eval-results__fail-icon">
                  <IconEmbedded name="remove_4" :size="16" color="rgba(173, 45, 45, 1)" />
                </span>
              </div>

              <div class="eval-results__text">
                <div class="eval-results__judge">{{ result.judgeType }}</div>
                <div class="eval-results__meta">
                  <span v-if="result.confidence != null" class="eval-results__confidence">Confidence: {{ result.confidence }}</span>
                </div>
              </div>
            </div>

            <div class="eval-results__right">
              <InfoIconWithTooltip v-if="result.scoreJustification" class="eval-results__info" :width="320"> <b>Justification:</b><br />{{ result.scoreJustification }} </InfoIconWithTooltip>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// Components
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import InfoIconWithTooltip from "@components/ui/InfoIconWithTooltip.vue";

// Types
import { EvalResultData } from "@contracts/shareStop";
import { computed } from "vue";

const props = defineProps<{
  evalResults: Array<EvalResultData> | undefined;
}>();

const groupedResults = computed(() => {
  if (!props.evalResults?.length) return [];

  const groups = new Map<string, Array<EvalResultData>>();

  for (const result of props.evalResults) {
    const runId = result.runId || "no-run-id";
    const existing = groups.get(runId);
    if (existing) {
      existing.push(result);
    } else {
      groups.set(runId, [result]);
    }
  }

  return Array.from(groups.entries()).map(([runId, results]) => ({
    runId: runId === "no-run-id" ? undefined : runId,
    results,
  }));
});

function getScoreClass(score: number): string {
  if (score >= 9) return "eval-results__score--excellent";
  if (score >= 7) return "eval-results__score--good";
  if (score >= 4) return "eval-results__score--ok";
  return "eval-results__score--poor";
}
</script>

<style scoped lang="scss">
.eval-results {
  margin: 6px 0 0;

  &__groups {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__group {
    display: flex;
    flex-direction: column;
  }

  &__group-header {
    font-size: 10px;
    color: rgba(0, 0, 0, 0.45);
    margin-bottom: 4px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  &__list {
    padding: 0;
    margin: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 8px;
    border: 1px rgba(0, 0, 0, 0.08) solid;
    border-radius: 6px;
    background: #fafafa;
  }

  &__left {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__judge {
    font-weight: 600;
    font-size: 12px;
  }

  &__meta {
    color: rgba(0, 0, 0, 0.6);
    font-size: 11px;
  }

  &__right {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__text {
    display: flex;
    flex-direction: column;
  }

  &__score {
    min-width: 28px;
    height: 28px;
    padding: 0 8px;
    border-radius: 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 15px;
    color: white;
  }

  &__score--excellent {
    background: linear-gradient(0deg, rgba(36, 137, 67, 1), rgba(36, 137, 67, 1));
  }
  &__score--good {
    background: linear-gradient(0deg, rgba(66, 160, 73, 1), rgba(66, 160, 73, 1));
  }
  &__score--ok {
    background: linear-gradient(0deg, rgba(219, 160, 29, 1), rgba(219, 160, 29, 1));
  }
  &__score--poor {
    background: linear-gradient(0deg, rgba(173, 45, 45, 1), rgba(173, 45, 45, 1));
  }

  &__passfail {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 14px;
    background: rgba(0, 0, 0, 0.04);
  }

  &__fail-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  &__info {
    opacity: 0.75;
  }
}
</style>
